import type { TimeSlot } from "@frontend/lib/actions/actions";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { Calendar } from "@frontend/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/shared/components/ui/popover";
import { addDays, addYears } from "date-fns";
import { CalendarPlus } from "lucide-react";
import { countBookableSlotsPerDay } from "../bookable-slots";
import { useBooking } from "../context/BookingContext";
import {
  DEFAULT_BOOKING_LEAD_TIME_DAYS,
  getNext5Days,
  getStartOfTodayInTimezone,
} from "../utils";
import { DateStrip } from "./DateStrip";

interface Provider {
  label: string;
  title?: string;
  value: string;
  img: string | null;
}

export function DateSelector({
  timeSlots,
  providers,
  timezone = "EST",
  leadTimeDays = DEFAULT_BOOKING_LEAD_TIME_DAYS,
  earliestBookableTime,
}: {
  timeSlots: TimeSlot[];
  providers: Provider[];
  timezone: string;
  leadTimeDays?: number;
  earliestBookableTime: Date;
}) {
  const { currentDisplayDates, setCurrentDisplayDates } = useBooking();
  const t = useTranslations("Shop");
  const availableProviderIds = providers.map((provider) =>
    Number.parseInt(provider.value),
  );
  const slotsByDay = countBookableSlotsPerDay(
    timeSlots,
    availableProviderIds,
    earliestBookableTime,
  );
  const startOfToday = getStartOfTodayInTimezone(timezone);
  const earliestBookableDay = addDays(startOfToday, leadTimeDays);

  return (
    <div className="sticky top-14 lg:top-20 z-30 bg-[#FAFAFA] w-full self-stretch shadow-[0_2px_8px_-4px_rgba(0,0,0,0.08)]">
      <div className="flex flex-row items-center w-full gap-8 sm:py-4 lg:py-2 lg:pr-4 lg:pl-0 sm:px-2">
        <div className="lg:w-[23%] sm:w-fit flex-col gap-2 flex justify-center lg:relative lg:top-0 sm:absolute sm:right-0 sm:top-[-48px]">
          <Popover>
            <PopoverTrigger className="flex bg-black rounded-md px-2 gap-2 items-center py-3 justify-center w-full">
              <CalendarPlus color="white" size={14} />
              <p className="text-white text-xs font-normal">
                {t("SelectDate")}
              </p>
            </PopoverTrigger>
            <PopoverContent className="p-0 border-0">
              <Calendar
                mode="single"
                selected={currentDisplayDates[0]}
                onSelect={(newValue) => {
                  if (newValue) {
                    setCurrentDisplayDates(getNext5Days(newValue));
                  }
                }}
                className="border rounded-md w-full"
                showOutsideDays={false}
                captionLayout="dropdown"
                fromDate={earliestBookableDay}
                toDate={addYears(new Date(), 1)}
              />
            </PopoverContent>
          </Popover>
        </div>

        <DateStrip
          getNext5Days={getNext5Days}
          slotsByDay={slotsByDay}
          timezone={timezone}
        />
      </div>
    </div>
  );
}
